/**
 * Phase 10 R4 → R5 — TicketFinancialSummaryCard
 *
 * Display-only ticket Gross Profit card. Reads from useTicketFinancialSummary
 * for the bucket math, and from useTicketFinancialWarnings for the unified
 * warning stack (target misses, low-margin floor, negative GP, completeness).
 *
 * Self-hides when summary is null or feature/role-gated off.
 *
 * UI-only: never persists, never mutates, never feeds the scheduler.
 */
import { AlertTriangle, AlertOctagon, CheckCircle2, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useTicketFinancialSummary } from '@/hooks/labor/useTicketFinancialSummary';
import { useTicketFinancialWarnings } from '@/hooks/labor/useTicketFinancialWarnings';
import type {
  CompletenessStatus,
  FinancialBucket,
  TicketFinancialSummary,
} from '@/lib/labor/computeTicketFinancialSummary';
import type {
  FinancialWarning,
  WarningSeverity,
} from '@/lib/labor/computeFinancialWarnings';
import { useShopFinancialSettings } from '@/hooks/labor/useShopFinancialSettings';

const fmt = (n: number) =>
  n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });

const fmtPct = (n: number) => `${n.toFixed(1)}%`;

interface Props {
  ticketId: string;
}

export function TicketFinancialSummaryCard({ ticketId }: Props) {
  const { summary } = useTicketFinancialSummary(ticketId);
  const { warnings } = useTicketFinancialWarnings(ticketId);
  const { settings } = useShopFinancialSettings();
  if (!summary) return null;
  return (
    <SummaryView
      summary={summary}
      warnings={warnings}
      targets={{
        parts: settings.target_parts_gp_percent,
        labor: settings.target_labor_gp_percent,
        total: settings.target_total_gp_percent,
      }}
    />
  );
}

function SummaryView({
  summary,
  warnings,
  targets,
}: {
  summary: TicketFinancialSummary;
  warnings: FinancialWarning[];
  targets: { parts: number | null; labor: number | null; total: number | null };
}) {
  const { parts, labor, combined, completeness } = summary;
  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">Ticket Financial Summary</h3>
          <StatusPill status={completeness.overallStatus} />
        </div>
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Gross profit · not net
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Section
          title="Parts"
          bucket={parts}
          status={completeness.partsStatus}
          marginTone={marginTone(parts.gpAmount, parts.gpPercent, parts.sell, targets.parts)}
        />
        <Section
          title="Labor"
          bucket={labor}
          status={worse(completeness.laborSellStatus, completeness.laborCostStatus)}
          marginTone={marginTone(labor.gpAmount, labor.gpPercent, labor.sell, targets.labor)}
        />
        <Section
          title="Combined"
          bucket={combined}
          status={completeness.overallStatus}
          marginTone={marginTone(
            combined.gpAmount,
            combined.gpPercent,
            combined.sell,
            targets.total,
          )}
          emphasized
        />
      </div>

      {warnings.length > 0 && (
        <div className="space-y-1 border-t pt-2">
          {warnings.map((w, i) => (
            <WarningRow key={i} w={w} />
          ))}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground border-t pt-2">
        Excludes taxes, fees, overhead. Not net profit.
      </p>
    </div>
  );
}

function Section({
  title,
  bucket,
  status,
  marginTone,
  emphasized,
}: {
  title: string;
  bucket: FinancialBucket;
  status: CompletenessStatus;
  marginTone: 'good' | 'warn' | 'critical' | 'neutral';
  emphasized?: boolean;
}) {
  return (
    <div
      className={
        'rounded-md border p-3 space-y-1.5 ' +
        (emphasized ? 'bg-muted/30' : 'bg-muted/10')
      }
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
        <div className="flex items-center gap-1.5">
          <MarginIcon tone={marginTone} />
          <StatusPill status={status} compact />
        </div>
      </div>
      <Row label="Sell" value={fmt(bucket.sell)} />
      <Row label="Cost" value={fmt(bucket.cost)} />
      <div className="border-t pt-1.5 mt-1 space-y-1">
        <Row label="GP" value={fmt(bucket.gpAmount)} bold tone={marginTone} />
        <Row label="GP%" value={fmtPct(bucket.gpPercent)} bold tone={marginTone} />
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  tone,
}: {
  label: string;
  value: string;
  bold?: boolean;
  tone?: 'good' | 'warn' | 'critical' | 'neutral';
}) {
  const toneClass =
    tone === 'critical'
      ? 'text-destructive'
      : tone === 'warn'
        ? 'text-warning'
        : tone === 'good'
          ? 'text-success'
          : '';
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular-nums ${bold ? 'font-semibold' : ''} ${bold ? toneClass : ''}`.trim()}>
        {value}
      </span>
    </div>
  );
}

function WarningRow({ w }: { w: FinancialWarning }) {
  const Icon =
    w.severity === 'critical'
      ? AlertOctagon
      : w.severity === 'warn'
        ? AlertTriangle
        : Info;
  const toneClass =
    w.severity === 'critical'
      ? 'text-destructive'
      : w.severity === 'warn'
        ? 'text-warning'
        : 'text-muted-foreground';
  return (
    <div className={`flex items-start gap-2 text-xs ${toneClass}`}>
      <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span>{w.message}</span>
    </div>
  );
}

function MarginIcon({
  tone,
}: {
  tone: 'good' | 'warn' | 'critical' | 'neutral';
}) {
  if (tone === 'critical')
    return <AlertOctagon className="h-3.5 w-3.5 text-destructive" aria-label="Negative" />;
  if (tone === 'warn')
    return <AlertTriangle className="h-3.5 w-3.5 text-warning" aria-label="Below target" />;
  if (tone === 'good')
    return <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-label="On target" />;
  return null;
}

function StatusPill({
  status,
  compact,
}: {
  status: CompletenessStatus;
  compact?: boolean;
}) {
  const label =
    status === 'complete'
      ? 'Complete'
      : status === 'partial'
        ? 'Partial'
        : 'Insufficient';
  const variant: 'default' | 'secondary' | 'destructive' =
    status === 'complete'
      ? 'default'
      : status === 'partial'
        ? 'secondary'
        : 'destructive';
  return (
    <Badge
      variant={variant}
      className={compact ? 'text-[10px] px-1.5 py-0 h-4' : 'text-[10px]'}
    >
      {label}
    </Badge>
  );
}

function worse(a: CompletenessStatus, b: CompletenessStatus): CompletenessStatus {
  if (a === 'insufficient' || b === 'insufficient') return 'insufficient';
  if (a === 'partial' || b === 'partial') return 'partial';
  return 'complete';
}

/**
 * Tone for a margin section:
 *   - critical: GP < 0
 *   - warn:     target set and GP% < target
 *   - good:     target set and GP% >= target
 *   - neutral:  no sell or no target → no opinion
 */
function marginTone(
  gpAmount: number,
  gpPercent: number,
  sell: number,
  target: number | null,
): 'good' | 'warn' | 'critical' | 'neutral' {
  if (sell <= 0) return 'neutral';
  if (gpAmount < 0) return 'critical';
  if (target == null) return 'neutral';
  return gpPercent < target ? 'warn' : 'good';
}
